import { useTranslation } from 'react-i18next';
import { Select } from '@/shared/ui';
import { SegmentedControl } from '@/shared/ui/components/basic/SegmentedControl';
import type { HistoryFilters, HistorySortKey } from '../context/partnerships-history-types';
import type { SearchStatus } from '../context/partnerships-wizard-types';

const STATUS_VALUES: Array<SearchStatus | 'all'> = [
  'all',
  'pending',
  'running',
  'completed',
  'failed',
];

const SORT_VALUES: HistorySortKey[] = ['recent', 'oldest', 'results'];

interface HistoryFilterBarProps {
  filters: HistoryFilters;
  onChange: (patch: Partial<HistoryFilters>) => void;
}

/**
 * Status and sort controls for the partnerships history page. Text search
 * lives in the page header's search bar, not here.
 *
 * @param props - Current filters and change handler.
 * @returns The filter bar row.
 */
export function HistoryFilterBar({ filters, onChange }: HistoryFilterBarProps) {
  const { t } = useTranslation('features/partnerships');

  return (
    <div data-tour="partnerships-history-filters" className="flex flex-wrap items-end gap-[var(--space-3)]">
      <div className="w-[140px]">
        <Select
          label={t('history.filters_status')}
          value={filters.status}
          onChange={(v) => onChange({ status: v as SearchStatus | 'all' })}
          data={STATUS_VALUES.map((v) => ({
            value: v,
            label: v === 'all' ? t('history.filters_all') : t(`history.status_${v}`),
          }))}
        />
      </div>
      <div className="w-auto">
        <SegmentedControl
          value={filters.sort}
          onChange={(v) => onChange({ sort: v as HistorySortKey })}
          items={SORT_VALUES.map((v) => ({
            value: v,
            label: t(`history.sort_${v}`),
          }))}
          variant="solid"
          size="sm"
          ariaLabel={t('history.filters_sort')}
        />
      </div>
    </div>
  );
}
